import { useCallback, useMemo, FC } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { IPointsCategoryReqData } from 'stores/categories/types';
import { StatusTypeReq } from 'types/common';
import { ReactComponent as EditIcon } from 'assets/edit_icon.svg';
import { pointsCategoriesTableTranslations } from './translations';
import { PointsCategoriesTableTransFields } from './types';
import styles from './pointsCategoriesTable.module.scss';
import { ComponentWithTooltip } from 'components/Core/ComponentWithTooltip';
import { StatusCell } from 'components/Core/StatusCell';

export const PointsCategoriesTable: FC = () => {
  const {
    mainStore: { mobileLanguages },
    categoriesStore: { pointsCategories },
  } = useStore();

  const { t } = useMyTranslations<PointsCategoriesTableTransFields>([pointsCategoriesTableTranslations.name]);

  const navigate = useNavigate();

  /**
   * The row switch is disabled: status is changed on the category page, where
   * the fallback picker lives (MOB1-2120). StatusCell still wants a handler.
   */
  const changeStatus = useCallback(async ({ id }: StatusTypeReq) => id, []);

  // const [filtred, setFiltred] = useState<IPointRequest[]>([]);

  const columns: Column<IPointsCategoryReqData>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { id, categoryName, categoryImage, categoryImageDark },
          },
        }: CellProps<IPointsCategoryReqData>) => {
          return (
            <div className={styles.avatar} onClick={() => navigate(`${id}`)}>
              <AvatarIcon avatar={categoryImage} name='logoUrl' size={24} styleProps={{ borderRadius: 8 }} />
              {/* Dark-theme icon on the mobile app's dark screen colour (darkPalette.backdrop). */}
              <AvatarIcon
                avatar={categoryImageDark}
                name='logoUrl'
                size={24}
                styleProps={{ borderRadius: 8, backgroundColor: '#0c111d' }}
              />
              <p className={styles.name}>{categoryName}</p>
            </div>
          );
        },
        accessor: ({ categoryName }) => categoryName,
        width: 4,
      },
      {
        Header: t('numberOfTranslations'),
        id: 'numberOfTranslations',
        Cell: ({
          row: {
            original: { numberOfTranslations },
          },
        }: CellProps<IPointsCategoryReqData>) => {
          return (
            <div
              className={`${styles.colorCell} ${
                numberOfTranslations < (mobileLanguages?.length || 0) && styles.errorCell
              }`}>
              <span>
                {numberOfTranslations} / {mobileLanguages?.length || 0}
              </span>
            </div>
          );
        },
        accessor: ({ numberOfTranslations }) => numberOfTranslations !== mobileLanguages?.length || 0,
        width: 2,
      },
      {
        Header: t('status'),
        id: 'status',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IPointsCategoryReqData>) => (
          <StatusCell id={id} status={status} changeStatus={changeStatus} disabled />
        ),
        accessor: ({ status }) => status,
        width: 1,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({ row: { original } }: CellProps<IPointsCategoryReqData>) => (
          <div className={styles.actionBoxBtn}>
            <ComponentWithTooltip
              component={
                <button className={styles.editBtn} onClick={() => navigate(`${original.id}`)} disabled={false}>
                  <EditIcon />
                </button>
              }
              name='editIcon'
              message='edit'
              place='top'
            />
          </div>
        ),
        width: 1,
        disableSortBy: true,
      },
    ],
    [mobileLanguages, pointsCategories, changeStatus]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return pointsCategories && pointsCategories.length > 0 ? (
    <>
      {/* <RequestsTableFiltersForm data={pointsCategories} setFiltredData={setFiltred} /> */}
      <Table
        columns={columns as never}
        data={[...pointsCategories]}
        simpleList
        isPaginated={pointsCategories.length > PAGE_SIZE}
      />
    </>
  ) : (
    <NoDataComponent />
  );
};

export default observer(PointsCategoriesTable);
